import React, { ReactNode, useMemo, useState } from "react";
import {
  Button,
  Col,
  Collapse,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
} from "antd";
import type { Moment } from "moment";
import type { FormProps } from "antd/es/form/Form";
import { displayFormat } from "../../../lib/utils/formatDate";
import { useAppSelector } from "../../..";
import { checkIsLoading, getSelectOptions } from "../../../lib/utils";
import { FormItemInputId } from "../../common";
import { booleanDictionary } from "../../../lib/const";
import type {
  Agreement,
  EmptyFunction,
  ISearchFilterField,
  LoadingStatusCode,
} from "../../../lib/types";
import type { IOfferListSearch } from "../../../store/slices/offering/types";
import { BulkFiltersToggler } from "../../BulkFiltersToggler/BulkFiltersToggler";
import "./OfferListSearch.less";

interface ComponentProps {
  isSearchOpen: boolean;
  toggleSearchOpen: EmptyFunction;
  onSearch: (paginationProps: { page: number; size?: number }) => void;
  onResetSearch: EmptyFunction;
  onChangeSearchValue: (field: Partial<IOfferListSearch<Moment>>) => void;
  // searchListValues: IOfferListSearch<Moment>;
  offerListLoading: LoadingStatusCode;
  filterName: string; // имя используемое как ключ localStorage для сохранения списка открытых полей поискового фильтра
  disabledSearchFields?: string[];
  selectedAgreement?: Agreement;
  disableOnDateFn?: (date: Moment) => boolean;
  form: FormInstance<any>;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const OfferListSearch = (props: ComponentProps) => {
  const {
    isSearchOpen,
    toggleSearchOpen,
    onSearch,
    onResetSearch,
    onChangeSearchValue,
    // searchListValues,
    offerListLoading,
    filterName,
    disabledSearchFields = [],
    selectedAgreement,
    disableOnDateFn,
    form,
  } = props;
  const { offeringStatuses, offeringStatusesLoading } = useAppSelector((state) => state.dicts);
  const {
    paymentType,
    paymentTypeLoading,
    tariffModelTypes,
    tariffModelTypesLoading,
    dicCurrencies,
  } = useAppSelector((state) => state.rtkDictionaries);

  const agreementTitle = useMemo((): ReactNode | "" => {
    if (selectedAgreement) {
      const agreementData = [
        selectedAgreement.id,
        selectedAgreement.agreementNumber,
        selectedAgreement.account?.organization?.fullName,
      ].filter(Boolean);
      return agreementData.map((item, idx) => <span key={idx}>{item}</span>);
    }
    return "";
  }, [selectedAgreement]);

  // const [form] = Form.useForm();

  const onFinish: FormProps["onFinish"] = () => {
    onSearch({
      page: 0,
    });
    onChangeSearchValue({ page: 0 });
  };

  const allFields: ISearchFilterField[] = useMemo(
    () => [
      {
        label: "Дата",
        fieldElement: (
          <Form.Item label="Дата" name="onDate">
            <DatePicker
              format={displayFormat}
              allowClear={false}
              disabled={disabledSearchFields.includes("onDate")}
              disabledDate={disableOnDateFn}
            />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "ID Предложения",
        fieldElement: (
          <FormItemInputId
            name="id"
            label="ID Предложения"
            allowClear
            className="form-item-required__hidden-error__mb0"
            placeholder={`Введите ID предложений через "," `}
            disabled={disabledSearchFields.includes("id")}
            visibleTooltip
          />
        ),
        visible: false,
      },
      {
        label: "Наименование",
        fieldElement: (
          <Form.Item label="Наименование" name="extName">
            <Input allowClear disabled={disabledSearchFields.includes("extName")} />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Код",
        fieldElement: (
          <Form.Item label="Код" name="name">
            <Input allowClear disabled={disabledSearchFields.includes("name")} />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Статус",
        fieldElement: (
          <Form.Item label="Статус" name="offeringStatusId">
            <Select
              options={getSelectOptions({
                list: offeringStatuses,
              })}
              loading={checkIsLoading(offeringStatusesLoading)}
              disabled={disabledSearchFields.includes("offeringStatusId")}
            />
          </Form.Item>
        ),
        visible: false,
      },
      {
        label: "Доступ к подкл.",
        fieldElement: (
          <Form.Item label="Доступ к подкл." name="isStandalone">
            <Select
              options={getSelectOptions({
                list: booleanDictionary,
              })}
              allowClear
              disabled={disabledSearchFields.includes("isStandalone")}
            />
          </Form.Item>
        ),
        visible: false,
      },
      {
        label: "ID Договора",
        fieldElement: (
          <Form.Item label="ID Договора" name="agreementId">
            {selectedAgreement ? (
              <Select allowClear>
                <Select.Option value={selectedAgreement?.id}>
                  <Tooltip
                    title={agreementTitle}
                    overlayClassName="offer-list-search__agreementData"
                    placement="bottom"
                  >
                    {selectedAgreement?.id}
                  </Tooltip>
                </Select.Option>
              </Select>
            ) : (
              <Input
                allowClear
                type="number"
                disabled={disabledSearchFields.includes("agreementId")}
              />
            )}
          </Form.Item>
        ),
        visible: false,
      },
      {
        label: "Тип оплаты",
        fieldElement: (
          <Form.Item label="Тип оплаты" name="paymentTypeId">
            <Select
              options={getSelectOptions({
                list: paymentType,
              })}
              allowClear
              loading={checkIsLoading(paymentTypeLoading)}
              disabled={disabledSearchFields.includes("paymentTypeId")}
            />
          </Form.Item>
        ),
        visible: false,
      },
      {
        label: "Бизнес-модель",
        fieldElement: (
          <Form.Item label="Бизнес-модель" name="tariffModelId">
            <Select
              options={getSelectOptions({
                list: tariffModelTypes,
              })}
              allowClear
              loading={checkIsLoading(tariffModelTypesLoading)}
              disabled={disabledSearchFields.includes("tariffModelId")}
            />
          </Form.Item>
        ),
        visible: false,
      },
    ],
    [booleanDictionary, offeringStatuses, paymentType, tariffModelTypes, dicCurrencies]
  );

  const [fields, setFields] = useState<ISearchFilterField[]>(allFields);

  return (
    <Collapse
      expandIconPosition="right"
      className="offer-list-search"
      activeKey={isSearchOpen ? "1" : ""}
      onChange={toggleSearchOpen}
    >
      <Collapse.Panel header="Расширенный поиск" key="1">
        <Row justify="end">
          <BulkFiltersToggler
            allFields={allFields}
            setFields={setFields}
            filterName={`offer-list-${filterName}`}
            minSave={["Дата"]}
          />
        </Row>
        <Form
          form={form}
          onValuesChange={onChangeSearchValue}
          onFinish={onFinish}
          wrapperCol={{ span: 15 }}
          labelCol={{ span: 8 }}
          labelAlign="left"
          // initialValues={searchListValues}
          onReset={() => {
            onResetSearch();
            // Сброс form по timeout потому, что initialValues берутся из searchListValues и они должны успеть сброситься
            // setTimeout(() => form.resetFields(), 0);
          }}
        >
          <Row gutter={50}>
            {fields
              .filter(({ visible }) => visible)
              .map(({ fieldElement }, idx) => (
                <Col span={8} key={idx}>
                  {fieldElement}
                </Col>
              ))}
          </Row>
          <Row justify="end">
            <Space size={20}>
              <Button htmlType="reset">Очистить</Button>
              <Spin spinning={checkIsLoading(offerListLoading)}>
                <Button type="primary" htmlType="submit">
                  Применить
                </Button>
              </Spin>
            </Space>
          </Row>
        </Form>
      </Collapse.Panel>
    </Collapse>
  );
};
